<template>
    <section ref="reviews" class="reviews">
        <div class="reviews__content">
            <h4>
                {{ $t('reviewsTitle') }}
                <template v-if="!isPreloader">({{ reviews.length }})</template>
            </h4>
            <div v-if="isPreloader" class="reviews__content__ratings --skeletons">
                <RatingPanel :isPreloader="isPreloader" :rating="0" :reviewsCount="0" :units="[]" />
                <List :isPreloader="isPreloader" :reviews="[]" />
            </div>
            <div v-else-if="reviews.length" class="reviews__content__ratings">
                <RatingPanel
                    :isPreloader="isPreloader"
                    :rating="rating"
                    :reviewsCount="reviews.length"
                    :units="ratingUnits"
                    @leave-review="openModalLeaveReview"
                />
                <List
                    :isPreloader="isPreloader"
                    :reviews="reviews"
                    :likedReviews="userLikedReviews"
                    :dislikedReviews="userDislikedReviews"
                    @review-rate-like="rateUserReview($event, true)"
                    @review-rate-dislike="rateUserReview($event, false)"
                />
            </div>
            <div v-else-if="!reviews.length" class="reviews__content__empty">
                <p>{{ $t('reviewsSectionEmptyDescription') }}</p>
                <MyActionBtn
                    :btnText="$t('reviewsActionCreateReview')"
                    :icon="'arrowRightIcon'"
                    :size="'medium'"
                    :theme="'dark'"
                    btnType="button"
                    @click="openModalLeaveReview"
                />
            </div>
        </div>

        <ModalSendProductReview :productId="productId" />
    </section>
</template>

<script>
import VueTypes from 'vue-types';
import { MODAL_SEND_PRODUCT_REVIEW } from '~/themes/novaporta/consts/modals';
import { createUnitItem, createUserReviewItem } from './types';
import { setReviewRate, getReviewsUserRated } from './utils/reviews-user-rate';
import eventBus from '@/plugins/event-bus';

export default {
    name: 'Reviews',
    props: {
        productId: VueTypes.number.isRequired
        // oldPrice: VueTypes.oneOfType([Number, nullable]).def(null),
    },
    data() {
        return {
            rating: null,
            ratingUnits: [],
            reviews: [],

            observer: null,

            userLikedReviews: [],
            userDislikedReviews: [],

            isPreloader: true,
            dataHaveBeenLoaded: false
        };
    },
    computed: {
        reviewsCount() {
            return 0; // Have to make dyanmoic
        }
    },
    methods: {
        openModal(name) {
            this.$store.dispatch('global/open', name);
        },
        openModalLeaveReview() {
            this.openModal(MODAL_SEND_PRODUCT_REVIEW);
        },

        sectionObserver(observerField, fetchMethod, sectionRef, threshold = 0.5) {
            const applyFetchMethod = entries => {
                entries.forEach(async entry => {
                    if (entry.isIntersecting) this[fetchMethod]();
                });
            };

            this[observerField] = new IntersectionObserver(applyFetchMethod, {
                root: null,
                rootMargin: '0px',
                threshold: threshold
            });
            this[observerField].observe(this.$refs[sectionRef]);
        },

        updatedRatedReviews() {
            if (getReviewsUserRated()) {
                const { likes, dislikes } = getReviewsUserRated();
                this.userLikedReviews = likes;
                this.userDislikedReviews = dislikes;
            }
        },
        async rateUserReview(reviewId, isLike) {
            try {
                const requestName = isLike ? 'postReviewLike' : 'postReviewDislike';
                await this.$api.product[requestName](reviewId);
                setReviewRate(reviewId, isLike);

                this.reviews = this.reviews.map(r => {
                    const newLikes = r.id === reviewId ? (isLike ? r.likes + 1 : r.likes) : r.likes;
                    const newDislikes = r.id === reviewId ? (isLike ? r.dislikes : r.dislikes + 1) : r.dislikes;

                    return {
                        ...r,
                        likes: newLikes,
                        dislikes: newDislikes
                    };
                });

                this.updatedRatedReviews();
            } catch (error) {
                eventBus.$emit('show-toast', {
                    message: this.$t('globalMessageSomethingWentWrong'),
                    type: 'failure'
                });
            }
        },

        async fetchReviews(productId = this.productId) {
            if (!this.dataHaveBeenLoaded) {
                this.isPreloader = true;
                try {
                    const { data } = await this.$api.product.getProductReviews(productId);
                    this.reviews = data.reviews.map(r => {
                        return createUserReviewItem({
                            id: r.id,
                            name: r.name,
                            text: r.text,
                            rate: r.rating,
                            likes: r.likes,
                            dislikes: r.dislikes,
                            date: r.created_at
                        });
                    });
                    this.rating = Number(parseFloat(data.rating.overall_rating).toFixed(2));
                    this.ratingUnits = data.rating.rating_units.reverse().map(u => {
                        return createUnitItem({
                            unit: u.unit,
                            value: u.value
                        });
                    });
                    this.isPreloader = false;
                    this.dataHaveBeenLoaded = true;
                } catch (error) {
                    eventBus.$emit('show-toast', {
                        message: this.$t('globalMessageSomethingWentWrong'),
                        type: 'failure'
                    });
                }
            }
        }
    },
    components: {
        RatingPanel: () => import('./components/RatingPanel'),
        List: () => import('./components/List'),
        MyActionBtn: () => import('@/themes/novaporta/components/ui/MyActionBtn'),

        ModalSendProductReview: () => import('@/themes/novaporta/components/modals/ModalSendProductReview')
    },
    mounted() {
        this.updatedRatedReviews();
        this.sectionObserver('observer', 'fetchReviews', 'reviews');
    }
};
</script>

<style lang="scss">
@import '~/themes/novaporta/assets/styles/utils.scss';
@import './styles.scss';
</style>
